"use client";

import Link from "next/link";
import React from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

export interface RealtorActionBarProps {
  /** CSS class name for custom styling */
  className?: string;
  /** Property ID for actions */
  propertyId: string;
  /** Realtor ID */
  realtorId: string;
  /** Whether actions are disabled */
  disabled?: boolean;
  /** Edit URL for navigation */
  editUrl?: string;
  /** Callback when archive action is triggered */
  onArchive?: () => void;
  /** Callback when promote action is triggered */
  onPromote?: () => void;
  /** Callback when duplicate action is triggered */
  onDuplicate?: () => void;
  /** Show only essential actions */
  compact?: boolean;
  /** Additional actions to render */
  additionalActions?: React.ReactNode;
}

/**
 * RealtorActionBar - Action bar component for realtor property management
 *
 * Figma Design Specifications:
 * - Container: 514×54px
 * - Edit Button: 210×54px, #CF1409, border-radius: 15px, white text, font-weight: 700
 * - Archive Button: 210×54px, white bg, #ECECEC border, border-radius: 20px, #373737 text, font-weight: 500
 * - Gap between buttons: 20px
 * - Shadow: 5px 5px 25px 15px rgba(235, 235, 235, 0.25)
 */
export const RealtorActionBar: React.FC<RealtorActionBarProps> = ({
  className,
  propertyId,
  realtorId,
  disabled = false,
  editUrl,
  onArchive,
  onPromote,
  onDuplicate,
  compact = false,
  additionalActions,
}) => {
  const t = useTranslations("realtor");

  return (
    <div
      className={cn(
        "flex items-center gap-5", // gap-5 = 20px between buttons
        {
          "opacity-50 pointer-events-none": disabled,
          "gap-2": compact,
        },
        className,
      )}
      role="toolbar"
      aria-label="Property management actions"
      data-testid="realtor-action-bar"
    >
      {editUrl ? (
        <Link
          href={editUrl}
          className={cn(
            "inline-flex items-center justify-center",
            "h-[54px] px-4", // Exact Figma dimensions
            "bg-[#CF1409] hover:bg-[#B91308] text-white", // Exact Figma color
            "rounded-[15px]", // Exact border-radius
            "font-bold text-[16px] leading-[20px]", // Figma typography
            "transition-colors duration-200",
            "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Figma shadow
            { "pointer-events-none opacity-50": disabled },
          )}
          data-testid="edit-button"
        >
          {t("actionBarEditText")}
        </Link>
      ) : (
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center",
            " h-[54px] px-4",
            "bg-[#CF1409] hover:bg-[#B91308] text-white",
            "rounded-[15px]",
            "font-bold text-[16px] leading-[20px]",
            "transition-colors duration-200",
            "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
          onClick={() => {}}
          disabled={disabled}
          data-testid="edit-button"
        >
          {t("actionBarEditText")}
        </button>
      )}

      {/* Белая кнопка "Отправить в архив" - 210×54px */}
      <button
        type="button"
        className={cn(
          "inline-flex items-center justify-center",
          " h-[54px] px-4", // Exact Figma dimensions
          "bg-white hover:bg-gray-50 text-[#373737]", // Exact Figma colors
          "border border-[#ECECEC]", // Exact border color
          "rounded-[20px]", // Different border-radius from edit button
          "font-medium text-[16px] leading-[20px]", // font-weight: 500
          "transition-colors duration-200",
          "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Same shadow
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
        onClick={() => {
          onArchive?.();
        }}
        disabled={disabled}
        data-testid="archive-button"
      >
        {t("actionBarArchiveText")}
      </button>

      {/* Additional actions */}
      {additionalActions && (
        <div className="flex-shrink-0">{additionalActions}</div>
      )}
    </div>
  );
};

export default RealtorActionBar;
